import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { NavigationOption } from "@intelligems/ig-types/schemas/ai/ui-actions/navigate";

import { IExperienceBuilderAiState } from "./types";

const initialState: IExperienceBuilderAiState = {
  open: true,
};

export const experienceBuilderAiSlice = createSlice({
  name: "experienceBuilderAi",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    clear: () => initialState,
    // Set when the experience-creation chat closes (see the creation-brief
    // capture listener); read + cleared by the first Viv turn.
    setCreationBrief: (state, action: PayloadAction<string | undefined>) => {
      state.creationBrief = action.payload;
    },
    setCreationBriefLastMessage: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.creationBriefLastMessage = action.payload;
    },
    clearCreationBrief: (state) => {
      state.creationBrief = undefined;
      state.creationBriefLastMessage = undefined;
    },
    setOriginExperienceCreationSessionId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.originExperienceCreationSessionId = action.payload;
    },
    _setAutoVerifyAttempt: (state, action: PayloadAction<number>) => {
      state.autoVerifyAttempt = action.payload;
    },
    resetAutoVerifyAttempt: (state) => {
      state.autoVerifyAttempt = 0;
    },
    // A turn spinning up claims a fresh token AND clears `continuationInFlight`
    // in one action, handing the busy lock off from any in-flight continuation
    // sequence atomically so `getIsAiBusy` never reads false between the two.
    beginTurn: (state, action: PayloadAction<number>) => {
      state.activeTurn = action.payload;
      state.continuationInFlight = false;
    },
    // Release the lock only if we're still the active turn. A turn whose
    // completion is processed after a newer turn already took over must NOT
    // clear the newer turn's token (the handoff race). Last-writer-wins, so a
    // dropped `endTurn` is overwritten by the next `beginTurn`, never leaked.
    endTurn: (state, action: PayloadAction<number>) => {
      if (state.activeTurn === action.payload) {
        state.activeTurn = undefined;
      }
    },
    setContinuationInFlight: (state, action: PayloadAction<boolean>) => {
      state.continuationInFlight = action.payload;
    },
    // Navigation picker (set when `navigate` returns `options`). The sheet
    // renders off `navigationChoice`; the `navigate` side-effect handler awaits
    // `navigationChoiceMade` / `navigationChoiceCancelled` to build its
    // continuation, then `clearNavigationChoice` tears the sheet down.
    showNavigationChoices: (
      state,
      action: PayloadAction<NavigationOption[]>
    ) => {
      state.navigationChoice = { options: action.payload };
    },
    navigationChoiceMade: (state, action: PayloadAction<NavigationOption>) => {
      if (!state.navigationChoice) {
        return;
      }
      state.navigationChoice.resolution = {
        kind: "selected",
        option: action.payload,
      };
    },
    navigationChoiceCancelled: (state) => {
      if (!state.navigationChoice) {
        return;
      }
      state.navigationChoice.resolution = { kind: "cancelled" };
    },
    clearNavigationChoice: (state) => {
      state.navigationChoice = undefined;
    },
  },
  selectors: {
    isOpen: (state) => state.open,
    getAutoVerifyAttempt: (state) => state.autoVerifyAttempt ?? 0,
    getCreationBrief: (state) => state.creationBrief,
    getCreationBriefLastMessage: (state) => state.creationBriefLastMessage,
    getOriginExperienceCreationSessionId: (state) =>
      state.originExperienceCreationSessionId,
    /** The active navigation picker, or undefined when none is pending. */
    getNavigationChoice: (state) => state.navigationChoice,
    /**
     * Global "AI is busy" signal. True while a chat turn is in flight OR a
     * continuation sequence is mid-flow (between turns of a multi-turn AI
     * flow). Visual-builder UI components consume this to disable controls
     * during AI turns; the chat's Stop button is the only escape hatch.
     */
    getIsAiBusy: (state) =>
      state.activeTurn !== undefined || state.continuationInFlight === true,
  },
});
